import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase, type AdminUser } from './supabase';

interface AuthState {
  loading: boolean;
  email: string | null;
  adminRow: AdminUser | null;
  isMaster: boolean;
  signOut: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [adminRow, setAdminRow] = useState<AdminUser | null>(null);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      if (session?.user?.email) {
        setEmail(session.user.email);
        await refreshAdminRow(session.user.email);
      }
      setLoading(false);
    };
    void init();

    // DİKKAT: onAuthStateChange callback'i içinde DOĞRUDAN `await supabase.from(...)`
    // çağırmak auth lock'unu tutar ve getSession()'ı kilitler (Supabase bilinen deadlock).
    // refreshAdminRow'u setTimeout(0) ile callback dışına defer ediyoruz.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const e = session?.user?.email ?? null;
      setEmail(e);
      if (e) {
        setTimeout(() => { void refreshAdminRow(e); }, 0);
      } else {
        setAdminRow(null);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const refreshAdminRow = async (e: string) => {
    // ilike (joker'siz) = büyük/küçük harf duyarsız tam eşleşme. Satır DB'de
    // "Semanur@..." gibi karışık harfle kayıtlıysa bile bulunur (eski .eq bunu kaçırıyordu).
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .ilike('email', e)
      .maybeSingle();

    // KRİTİK: Sorgu hata verirse (geçici ağ/RLS/JWT gecikmesi, özellikle
    // token yenileme anında) mevcut adminRow'u SIFIRLAMA — yoksa kullanıcı
    // her geçici hatada "hesap tanımlı değil" ekranına atılır ("sürekli atıyor").
    // Sadece sorgu BAŞARILI olup satır gerçekten yoksa null'a düşür.
    if (error) {
      console.warn('admin_users okunamadı (oturum korunuyor):', error.message);
      return;
    }
    setAdminRow(data as AdminUser | null);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setEmail(null);
    setAdminRow(null);
  };

  return (
    <AuthCtx.Provider value={{
      loading,
      email,
      adminRow,
      isMaster: !!adminRow?.is_master && !!adminRow?.is_active,
      signOut,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
